import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DEFAULT_FEATURE_FLAGS, FeatureFlags } from '../models/feature-flags.model';

/**
 * Loads and caches the current org's feature flags. Call `load()` once after
 * login. Components consume via:
 *   - `features$` — Observable<FeatureFlags> for async pipe in templates
 *   - `featuresSignal()` — Angular signal for computed expressions
 *   - `speedLimits` — convenience signal for the most common gate
 *
 * Failure is fail-safe: if the request errors we keep defaults (every flag
 * off), so any new feature gated by this service is hidden until backend
 * confirms it's enabled.
 */
@Injectable({ providedIn: 'root' })
export class OrgFeaturesService {
  private baseUrl = `${environment.apiUrl}/org`;
  private state = new BehaviorSubject<FeatureFlags>({ ...DEFAULT_FEATURE_FLAGS });
  private signalState = signal<FeatureFlags>({ ...DEFAULT_FEATURE_FLAGS });

  features$: Observable<FeatureFlags> = this.state.asObservable();
  featuresSignal = this.signalState.asReadonly();
  speedLimits = computed(() => this.signalState().speed_limits);

  constructor(private http: HttpClient) {}

  load(): Observable<FeatureFlags> {
    return this.http.get<{ features: Partial<FeatureFlags> }>(`${this.baseUrl}/features`).pipe(
      map(r => ({ ...DEFAULT_FEATURE_FLAGS, ...(r?.features || {}) })),
      tap(features => {
        this.state.next(features);
        this.signalState.set(features);
      }),
      catchError(() => of({ ...DEFAULT_FEATURE_FLAGS })),
    );
  }

  get current(): FeatureFlags {
    return this.state.value;
  }
}
